import { useEffect, useMemo, useState } from "react";
import { ChevronDown, Copy, Eye, Trash2 } from "lucide-react";
import {
  combineOffsetDaysHours,
  formatOffsetCompact,
  formatOffsetHours,
  splitOffsetHours,
} from "./formatOffset";
import { templateSelectLabel } from "./templateLabel";

const DELAY_PRESETS = [
  { label: "1 day", hours: 24 },
  { label: "2 days", hours: 48 },
  { label: "3 days", hours: 72 },
  { label: "1 week", hours: 168 },
];

function keepAnchor(step) {
  return step.anchor || "INVITATION_SENT";
}

function CadenceFlow({ steps }) {
  if (!steps.length) return null;

  return (
    <div className="rounded-lg border border-[#004f73]/15 bg-[#004f73]/[0.03] px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[#004f73]/70 mb-1.5">
        At a glance
      </p>
      <div className="flex flex-wrap items-center gap-x-1 gap-y-1 text-xs text-slate-700">
        <span className="font-medium text-slate-500">Invite</span>
        {steps.map((step, i) => (
          <span key={step._key} className="inline-flex items-center gap-1">
            <span className="text-slate-300" aria-hidden>
              →
            </span>
            <span
              className={`font-medium ${
                step.is_enabled ? "text-[#004f73]" : "text-slate-400 line-through"
              }`}
              title={step.label || `Reminder ${i + 1}`}
            >
              {formatOffsetCompact(step.offset_hours)}
            </span>
          </span>
        ))}
      </div>
      <p className="text-[10px] text-slate-400 mt-1.5">
        Each arrow is the wait before that reminder sends.
      </p>
    </div>
  );
}

function stepSummary(step, relativeTo) {
  const offsetLabel = formatOffsetHours(step.offset_hours);
  if (!offsetLabel) return `Set how long to wait after ${relativeTo}`;
  return `Wait ${offsetLabel}, then send`;
}

function StatusBadge({ enabled }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${
        enabled ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${enabled ? "bg-emerald-500" : "bg-slate-400"}`} />
      {enabled ? "On" : "Off"}
    </span>
  );
}

function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        checked ? "bg-[#004f73]" : "bg-slate-300"
      }`}
    >
      <span
        className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform shadow-sm"
        style={{ transform: checked ? "translateX(18px)" : "translateX(2px)" }}
      />
    </button>
  );
}

function DelayInput({ step, onUpdate }) {
  const { days, hours } = splitOffsetHours(step.offset_hours);
  const fieldId = `step-${step._key}`;

  const applyParts = (nextDays, nextHours) => {
    const total = combineOffsetDaysHours(nextDays, nextHours);
    onUpdate({
      ...step,
      anchor: keepAnchor(step),
      offset_hours: total === "" ? "" : total,
    });
  };

  const applyPreset = (presetHours) =>
    onUpdate({ ...step, anchor: keepAnchor(step), offset_hours: presetHours });

  return (
    <div className="space-y-2.5">
      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <label htmlFor={`${fieldId}-days`} className="block text-[11px] font-medium text-slate-500 mb-1">
            Days
          </label>
          <input
            id={`${fieldId}-days`}
            type="number"
            min="0"
            step="1"
            placeholder="0"
            className="w-16 border border-slate-200 rounded-lg px-2 py-2 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
            value={days}
            onChange={(e) => applyParts(e.target.value, hours)}
          />
        </div>
        <div>
          <label htmlFor={`${fieldId}-hours`} className="block text-[11px] font-medium text-slate-500 mb-1">
            Hours
          </label>
          <input
            id={`${fieldId}-hours`}
            type="number"
            min="0"
            max="23"
            step="0.5"
            placeholder="0"
            className="w-16 border border-slate-200 rounded-lg px-2 py-2 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/40"
            value={hours}
            onChange={(e) => applyParts(days, e.target.value)}
          />
        </div>
        <p className="text-[11px] text-slate-400 pb-2">or pick a preset</p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {DELAY_PRESETS.map((preset) => (
          <button
            key={preset.hours}
            type="button"
            onClick={() => applyPreset(preset.hours)}
            className={`text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
              Number(step.offset_hours) === preset.hours
                ? "border-[#004f73] bg-[#004f73] text-white font-medium"
                : "border-slate-200 text-slate-600 hover:border-slate-300 hover:bg-slate-50"
            }`}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function AssignedTemplateRow({ audience, templates, value, onChange, onPreview }) {
  const options = (templates || []).filter((t) => t.role_group === audience.key);
  return (
    <div className="flex items-center gap-2">
      <span className="w-24 shrink-0 truncate text-xs text-slate-600" title={audience.label}>
        {audience.label}
      </span>
      <select
        aria-label={`${audience.label} template`}
        className="flex-1 min-w-0 border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#004f73]/20"
        value={value || ""}
        onChange={(e) => {
          const id = e.target.value || null;
          onChange(id);
          if (id) onPreview?.(id, audience.label);
        }}
      >
        <option value="">Stage default</option>
        {options.map((t) => (
          <option key={t.id} value={t.id}>
            {templateSelectLabel(t, audience.label)}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="shrink-0 p-1.5 rounded-md text-[#004f73] hover:bg-[#004f73]/10 disabled:opacity-25"
        onClick={() => onPreview?.(value, audience.label)}
        disabled={!value}
        aria-label={`Preview ${audience.label} email`}
      >
        <Eye size={14} />
      </button>
    </div>
  );
}

function anchorIsImplemented(step, anchors) {
  const key = keepAnchor(step);
  const list = Array.isArray(anchors) ? anchors : [];
  if (!list.length) return key === "INVITATION_SENT";
  const meta = list.find((a) => a.key === key);
  return meta ? !!meta.implemented : key === "INVITATION_SENT";
}

function StepCard({
  step,
  displayIndex,
  relativeTo,
  isFirst,
  isLast,
  expanded,
  onToggleExpand,
  templates,
  audiences,
  anchors,
  onUpdate,
  onRemove,
  onDuplicate,
  onViewTemplate,
  allowAudienceTemplateOverrides = true,
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const templateIds = step.template_ids || {};
  const hasCustomTemplates = Object.values(templateIds).some(Boolean);
  const hasWindow = !!(step.dispatch_window_start && step.dispatch_window_end);
  const summary = stepSummary(step, relativeTo);
  const implemented = anchorIsImplemented(step, anchors);
  const audienceList = Array.isArray(audiences) ? audiences : [];

  const setTemplateFor = (audienceKey, templateId) =>
    onUpdate({
      ...step,
      anchor: keepAnchor(step),
      template_ids: { ...templateIds, [audienceKey]: templateId || undefined },
    });

  return (
    <div className="relative flex gap-3">
      <div className="flex flex-col items-center w-5 shrink-0 pt-1">
        <span
          className={`grid h-5 w-5 place-items-center rounded-full text-[10px] font-bold ${
            step.is_enabled ? "bg-[#004f73] text-white" : "bg-slate-200 text-slate-500"
          }`}
        >
          {displayIndex + 1}
        </span>
        {!isLast ? <div className="w-px flex-1 min-h-[12px] bg-slate-200 mt-1" aria-hidden /> : null}
      </div>

      <div
        className={`flex-1 min-w-0 mb-2 rounded-xl border transition-shadow ${
          expanded
            ? "border-[#004f73]/30 bg-white shadow-sm ring-1 ring-[#004f73]/10"
            : "border-slate-200 bg-slate-50/80 hover:border-slate-300 hover:bg-white"
        }`}
      >
        <button
          type="button"
          className="flex w-full items-start gap-2 px-3 py-3 text-left"
          onClick={onToggleExpand}
          aria-expanded={expanded}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-slate-900 truncate">
                {step.label || `Reminder ${displayIndex + 1}`}
              </span>
              <StatusBadge enabled={!!step.is_enabled} />
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              {isFirst ? "After invitation" : "After previous reminder"} · {summary}
            </p>
          </div>
          <ChevronDown
            size={16}
            className={`shrink-0 text-slate-400 mt-0.5 transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </button>

        {expanded ? (
          <div className="px-3 pb-3 space-y-3 border-t border-slate-100 pt-3">
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs font-medium text-slate-600">Step name</label>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-slate-500">Enabled</span>
                <Toggle
                  checked={!!step.is_enabled}
                  disabled={!implemented}
                  onChange={(v) => onUpdate({ ...step, anchor: keepAnchor(step), is_enabled: v })}
                />
              </div>
            </div>
            <input
              className="w-full text-sm border border-slate-200 rounded-lg px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20"
              value={step.label}
              onChange={(e) => onUpdate({ ...step, anchor: keepAnchor(step), label: e.target.value })}
              placeholder={`Reminder ${displayIndex + 1}`}
            />
            {!implemented ? (
              <p className="text-[11px] text-amber-600">
                This trigger is not available yet, so the step stays off.
              </p>
            ) : null}

            <div>
              <p className="text-xs font-medium text-slate-600 mb-2">How long to wait</p>
              <DelayInput step={step} onUpdate={onUpdate} />
            </div>

            <div className="flex items-center gap-1 pt-1">
              <button
                type="button"
                aria-label={`Duplicate ${step.label}`}
                onClick={onDuplicate}
                className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
              >
                <Copy size={13} /> Duplicate
              </button>
              <button
                type="button"
                aria-label={`Remove ${step.label}`}
                onClick={onRemove}
                className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg"
              >
                <Trash2 size={13} /> Remove
              </button>
            </div>

            <div className="border-t border-slate-100 pt-2">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 text-xs font-medium text-slate-600 hover:text-slate-900 py-1"
                aria-expanded={advancedOpen}
                onClick={() => setAdvancedOpen((v) => !v)}
              >
                <span>
                  Advanced
                  {hasWindow || hasCustomTemplates ? " · customized" : ""}
                </span>
                <ChevronDown
                  size={14}
                  className={`shrink-0 transition-transform ${advancedOpen ? "rotate-180" : ""}`}
                />
              </button>
              {advancedOpen ? (
                <div className="mt-3 space-y-4">
                  <div>
                    <label className="flex items-center gap-2 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        checked={hasWindow}
                        onChange={(e) =>
                          onUpdate({
                            ...step,
                            anchor: keepAnchor(step),
                            dispatch_window_start: e.target.checked
                              ? step.dispatch_window_start || "09:00"
                              : null,
                            dispatch_window_end: e.target.checked
                              ? step.dispatch_window_end || "17:00"
                              : null,
                          })
                        }
                        className="rounded border-slate-300 text-[#004f73]"
                      />
                      Only send during business hours
                    </label>
                    {hasWindow ? (
                      <div className="flex items-center gap-2 mt-2">
                        <input
                          type="time"
                          aria-label="Window start"
                          className="border border-slate-200 rounded-lg px-2 py-1 text-xs"
                          value={step.dispatch_window_start || "09:00"}
                          onChange={(e) =>
                            onUpdate({
                              ...step,
                              anchor: keepAnchor(step),
                              dispatch_window_start: e.target.value,
                              dispatch_window_end: step.dispatch_window_end || "17:00",
                            })
                          }
                        />
                        <span className="text-xs text-slate-400">to</span>
                        <input
                          type="time"
                          aria-label="Window end"
                          className="border border-slate-200 rounded-lg px-2 py-1 text-xs"
                          value={step.dispatch_window_end || "17:00"}
                          onChange={(e) =>
                            onUpdate({
                              ...step,
                              anchor: keepAnchor(step),
                              dispatch_window_start: step.dispatch_window_start || "09:00",
                              dispatch_window_end: e.target.value,
                            })
                          }
                        />
                      </div>
                    ) : null}
                  </div>

                  {allowAudienceTemplateOverrides ? (
                    <div>
                      <p className="text-xs font-medium text-slate-600 mb-1">Custom email per audience</p>
                      <p className="text-[11px] text-slate-500 mb-2">
                        Optional — leave as stage default to use the built-in email for this reminder number.
                      </p>
                      <div className="space-y-2">
                        {audienceList.map((a) => (
                          <AssignedTemplateRow
                            key={a.key}
                            audience={a}
                            templates={templates}
                            value={templateIds[a.key]}
                            onChange={(v) => setTemplateFor(a.key, v)}
                            onPreview={onViewTemplate}
                          />
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function ReminderStepList({
  steps = [],
  templates = [],
  audiences = [],
  anchors = [],
  onChange,
  onAddStep,
  onDuplicateStep,
  onViewTemplate,
  onSave,
  saving,
  canSave,
  saveTitle,
  allowAudienceTemplateOverrides = true,
}) {
  const sortedSteps = useMemo(
    () => [...steps].sort((a, b) => Number(a.offset_hours) - Number(b.offset_hours)),
    [steps]
  );

  const [expandedKey, setExpandedKey] = useState(() => sortedSteps[0]?._key ?? null);

  useEffect(() => {
    if (!expandedKey && sortedSteps.length) {
      setExpandedKey(sortedSteps[0]._key);
    }
  }, [expandedKey, sortedSteps]);

  const update = (key, next) => onChange(steps.map((s) => (s._key === key ? next : s)));
  const remove = (key) => {
    onChange(steps.filter((s) => s._key !== key));
    if (expandedKey === key) {
      const remaining = sortedSteps.filter((s) => s._key !== key);
      setExpandedKey(remaining[0]?._key ?? null);
    }
  };

  const handleAddStep = () => {
    const key = onAddStep?.();
    if (key) setExpandedKey(key);
  };

  return (
    <div className="space-y-4">
      {sortedSteps.length ? (
        <CadenceFlow steps={sortedSteps} />
      ) : (
        <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5 text-xs text-slate-600 leading-relaxed">
          Participants who haven&apos;t finished get each reminder once, in order, waiting the time you
          set between emails.
        </div>
      )}

      <div>
        {sortedSteps.map((step, i) => (
          <StepCard
            key={step._key}
            step={step}
            displayIndex={i}
            isFirst={i === 0}
            isLast={i === sortedSteps.length - 1}
            relativeTo={i === 0 ? "invitation sent" : "previous reminder"}
            expanded={expandedKey === step._key}
            onToggleExpand={() => setExpandedKey((prev) => (prev === step._key ? null : step._key))}
            templates={templates}
            audiences={audiences}
            anchors={anchors}
            onUpdate={(next) => update(step._key, next)}
            onRemove={() => remove(step._key)}
            onDuplicate={() => onDuplicateStep?.(step._key)}
            onViewTemplate={onViewTemplate}
            allowAudienceTemplateOverrides={allowAudienceTemplateOverrides}
          />
        ))}

        {!steps.length ? (
          <div className="rounded-xl border border-dashed border-slate-200 py-10 text-center">
            <p className="text-sm font-medium text-slate-600">No reminders configured</p>
            <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">
              Add a step to automatically follow up with participants who haven&apos;t completed their
              assessment.
            </p>
          </div>
        ) : null}
      </div>

      <button
        type="button"
        className="w-full py-2.5 text-sm font-medium text-[#004f73] border border-dashed border-[#004f73]/35 rounded-xl hover:bg-[#004f73]/5 transition-colors"
        onClick={handleAddStep}
      >
        + Add reminder
      </button>

      {onSave ? (
        <div className="sticky bottom-0 -mx-1 px-1 pt-2 pb-1 bg-gradient-to-t from-white via-white to-white/80">
          <button
            type="button"
            className="w-full py-2.5 text-sm font-semibold text-white bg-[#004f73] hover:bg-[#003a57] rounded-xl disabled:opacity-50 transition-colors shadow-sm"
            onClick={onSave}
            disabled={saving || !canSave}
            title={saveTitle || ""}
          >
            {saving ? "Saving…" : "Save schedule"}
          </button>
        </div>
      ) : null}
    </div>
  );
}
